import { type WeekStartsOn } from '@opallabs/concepts'
import { Button, ButtonIconPosition, Icon } from '@opallabs/lapidary'
import { DatePicker } from '@opallabs/satellite'
import * as styles from './styles'

export { selectors as dateNavigatorSelectors } from './styles'

export type DateNavigatorProps = Readonly<{
  /** The dates on show: "February 2026", "February 18-24", "Wednesday, 15 July". */
  label: string
  /** aria: "Go to next month". */
  nextLabel: string
  onDateSelect: (date: Date) => void
  onNext: () => void
  onPrevious: () => void
  onToday: () => void
  /** aria: "Go to previous month". */
  previousLabel: string
  /** The date the picker opens on. */
  selectedDate: Date
  /**
   * Whether a screenreader is told the label changed. Set it where the view
   * under the label redraws in place, as the month grid does.
   */
  shouldAnnounceLabel?: boolean
  weekStartsOn?: WeekStartsOn
}>

/**
 * Which dates a calendar is showing, and every way to move to others: a step
 * either side of a picker, then today.
 */
export const DateNavigator: React.FC<DateNavigatorProps> = ({
  label,
  nextLabel,
  onDateSelect,
  onNext,
  onPrevious,
  onToday,
  previousLabel,
  selectedDate,
  shouldAnnounceLabel = false,
  weekStartsOn,
}) => (
  <div className={styles.selectors.root} css={styles.root}>
    <Button
      ariaLabel={previousLabel}
      icon={<Icon ariaHidden={true} glyph="gem-chevron-w" />}
      name={previousLabel}
      onClick={onPrevious}
      variant="ghost"
    />
    <DatePicker
      ariaLabel={`${label}, jump to date`}
      ariaLive={shouldAnnounceLabel ? 'polite' : undefined}
      buttonText={label}
      buttonVariant="ghost"
      datePickerDisplayType="button"
      enableDateRemoval={false}
      iconPosition={ButtonIconPosition.leading}
      onDateSelect={(selected) => selected && onDateSelect(selected.dateObject)}
      selectedDate={selectedDate}
      showButtonText={true}
      weekStartsOn={weekStartsOn}
    />
    <Button
      ariaLabel={nextLabel}
      icon={<Icon ariaHidden={true} glyph="gem-chevron-e" />}
      name={nextLabel}
      onClick={onNext}
      variant="ghost"
    />

    <span aria-hidden={true} css={styles.divider} />

    <Button
      ariaLabel="Go to today"
      icon={<Icon ariaHidden={true} glyph="gem-calendar-today" />}
      name="Go to today"
      onClick={onToday}
      variant="ghost"
    />
  </div>
)
DateNavigator.displayName = 'Calendar: DateNavigator'
